/**
 * InsuranceDepositsAnalytics — what drivers paid toward their premiums, by the day they paid.
 *
 * OFF `insurance_loan_deposits`, WHICH THIS PAGE HAD STOPPED READING. The three cards read
 * `insurance_loan_repayments` — a live table, but the wrong subject — and stated GHS 5,312.00 over
 * one payment where the default window holds GHS 30,633.00 over seven and the estate holds GHS
 * 75,052.46 over twenty-six. `depositLedger.ts` holds the rules; this file draws them.
 *
 * THE BASIS IS `transactions.transaction_at`, DELIBERATELY UNLIKE PREMIUM YEARS. A deposit is dated
 * when the driver paid it; a premium year is dated by the year it covers, which is why its page
 * buckets on `insurances.starts_at`. Two of the twenty-six production deposits land before their
 * own policy starts, so the two pages are not meant to agree month for month.
 *
 * FOUR CARDS RATHER THAN THREE, AND THE COUNT MOVES INTO THE FIRST ONE. "Deposit Count" said
 * nothing "{n} deposits" underneath the total did not already say; the room it frees states the
 * split the estate actually turns on — what these payments went toward. A payment against a premium
 * Primelane advanced reduces the interest base; a payment against a premium the driver paid in full
 * bought the year outright [D-B45, D-B46].
 *
 * ONE QUERY, TWO SUBSCRIBERS. Both tabs read `DEPOSIT_LEDGER_KEY`, so react-query keeps ONE cache
 * entry and at most one request in flight however many of them are mounted: the tab mounted second
 * subscribes to the query the first one made and renders the array that is already there rather
 * than waiting on a fetch of its own. That is not a frozen answer — `App.tsx:61-66` sets no
 * `staleTime`, so a tab remounting after the switch also refetches in the background and re-renders
 * when the answer lands. What the shared key buys is one query rather than two, not the absence of
 * a second fetch.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BadgeCheck, Landmark, TrendingUp, Wallet } from 'lucide-react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { businessToday, formatCurrency } from '@/lib/format';
import {
    DEPOSIT_LEDGER_KEY,
    depositMonthly,
    depositSummary,
    fetchDepositLedger,
    withinDepositWindow,
} from './depositLedger';

export default function InsuranceDepositsAnalytics() {
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    const { data: deposits, isLoading, error } = useQuery({
        queryKey: DEPOSIT_LEDGER_KEY,
        queryFn: fetchDepositLedger,
    });

    const { summary, monthlyData } = useMemo(() => {
        const inWindow = (deposits ?? []).filter((row) =>
            withinDepositWindow(row, fromDate, toDate),
        );
        const monthKeys = eachMonthOfInterval({ start: monthRange.from, end: monthRange.to }).map(
            (month) => format(month, 'yyyy-MM'),
        );

        return {
            summary: depositSummary(inWindow),
            monthlyData: depositMonthly(inWindow, monthKeys).map((point) => ({
                ...point,
                // Labelled off the key's own parts — never `new Date(key + '-01')`, which reads
                // UTC midnight in the viewer's zone and puts every bar a month early west of
                // Greenwich.
                month: format(
                    new Date(Number(point.monthKey.slice(0, 4)), Number(point.monthKey.slice(5, 7)) - 1, 1),
                    'MMM yy',
                ),
            })),
        };
    }, [deposits, fromDate, toDate, monthRange.from, monthRange.to]);

    // A FAILED OR TRUNCATED READ STATES NOTHING. Every card here is a Σ or a count over the whole
    // ledger, so rendering them off a response the server cut short would print four wrong totals
    // under confident headings — and a cut through one loan's deposits also re-decides which of
    // the two middle cards its payments belong in. `fetchDepositLedger` throws on
    // `rows.length < count`; this is where an admin reads why.
    if (error) {
        return <p className="text-center text-destructive py-8">{error.message}</p>;
    }

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
                </div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Deposited</CardTitle>
                        <Wallet className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-green-600">
                            {formatCurrency(summary.totalDeposited)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {summary.depositCount} deposits
                        </p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Toward Advances</CardTitle>
                        <Landmark className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summary.towardAdvances)}</div>
                        <p className="text-xs text-muted-foreground">
                            {summary.advanceCount} part-payments · reduces the interest base
                        </p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Paid in Full</CardTitle>
                        <BadgeCheck className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summary.paidInFull)}</div>
                        <p className="text-xs text-muted-foreground">
                            {summary.paidInFullCount} payments on premiums the driver paid for
                        </p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Avg Deposit</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summary.avgDeposit)}</div>
                        <p className="text-xs text-muted-foreground">Per deposit</p>
                    </CardContent>
                </Card>
            </div>

            <Card className="border border-border/40">
                <CardHeader>
                    <CardTitle>Monthly Insurance Deposits</CardTitle>
                    <CardDescription>What drivers paid toward their premiums, by payment date</CardDescription>
                </CardHeader>
                <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                        {/* ONE STACK, TWO SERIES. A shared `stackId` is what makes the bands add up
                            to the month's whole deposit volume; two ids would draw two charts on
                            one axis and overstate every month by the overlap. */}
                        <AreaChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis dataKey="month" className="text-xs" />
                            <YAxis className="text-xs" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip
                                formatter={(value: number) => formatCurrency(value)}
                                contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            />
                            <Legend />
                            <Area type="monotone" dataKey="towardAdvances" stackId="deposits" stroke="hsl(var(--chart-1))" fill="hsl(var(--chart-1))" fillOpacity={0.6} name="Toward advances" />
                            <Area type="monotone" dataKey="paidInFull" stackId="deposits" stroke="hsl(var(--chart-2))" fill="hsl(var(--chart-2))" fillOpacity={0.6} name="Paid in full" />
                        </AreaChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
        </div>
    );
}
